import React from 'react';
import footerContent from 'platform/forms/components/FormFooter';
import { VA_FORM_IDS } from 'platform/forms/constants';
import {
  profilePersonalInfoPage,
  profileContactInfoPages,
} from 'platform/forms-system/src/js/patterns/prefill';
import { getContent } from 'platform/forms-system/src/js/utilities/data/profile';
import environment from '~/platform/utilities/environment';
import { TITLE, SUBTITLE } from '../constants';
import manifest from '../manifest.json';
import { CustomReviewTopContent } from '../helpers';

import prefillTransform from './prefillTransform';
import submitForm from './submitForm';
import transform from './transform';

// Components
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import PersonalInformationNote from '../components/PersonalInformationNote';
import PresubmitInfo from '../components/PresubmitInfo';

// Pages
import {
  educationBenefitsEligibility,
  hasPreviouslyApplied,
  selectVaBenefit,
  payeeNumber,
  examNameAndDateTaken,
  organizationInfo,
  examCost,
  remarks,
  supportingDocuments,
} from '../pages';

export const SUBMIT_URL = `${environment.API_URL}/v0/education_benefits_claims/0810`;

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: SUBMIT_URL,
  submit: submitForm,
  transformForSubmit: transform,
  trackingPrefix: '0810-edu-benefits',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  dev: {
    showNavLinks: true,
    collapsibleNavLinks: true,
    disableWindowUnloadInCI: true,
  },
  formId: VA_FORM_IDS.FORM_22_0810,
  saveInProgress: {
    messages: {
      inProgress: 'Your request (22-0810) is in progress.',
      expired:
        'Your saved request (22-0810) has expired. Please start a new request.',
      saved: 'Your request has been saved.',
    },
  },
  version: 0,
  prefillEnabled: true,
  prefillTransformer: prefillTransform,
  preSubmitInfo: {
    CustomComponent: PresubmitInfo,
    field: 'statementOfTruthCertified',
    statementOfTruth: {
      useProfileFullName: true,
    },
  },
  CustomReviewTopContent,
  savedFormMessages: {
    notFound: 'Please start over.',
    noAuth: 'Please sign in again to continue your request.',
  },
  title: TITLE,
  subTitle: SUBTITLE,
  customText: {
    reviewPageTitle: 'Review',
    submitButtonText: 'Continue',
  },
  defaultDefinitions: {},
  useCustomScrollAndFocus: true,
  chapters: {
    educationBenefitsChapter: {
      title: 'Your education benefits information',
      pages: {
        hasPreviouslyApplied: {
          path: 'previously-applied',
          title: 'Your VA education benefits',
          uiSchema: hasPreviouslyApplied.uiSchema,
          schema: hasPreviouslyApplied.schema,
          updateFormData: hasPreviouslyApplied.updateFormData,
        },
        selectVABenefit: {
          path: 'select-va-benefit-program',
          title: 'Select a VA benefit program',
          uiSchema: selectVaBenefit.uiSchema,
          schema: selectVaBenefit.schema,
          depends: formData => formData?.hasPreviouslyApplied === true,
        },
        educationBenefitsEligibility: {
          path: 'education-benefits-eligibility',
          title: 'Your VA education benefits',
          uiSchema: educationBenefitsEligibility.uiSchema,
          schema: educationBenefitsEligibility.schema,
          depends: formData => formData?.hasPreviouslyApplied === false,
        },
      },
    },
    personalInformationChapter: {
      title: 'Your personal information',
      pages: {
        ...profilePersonalInfoPage({
          personalInfoConfig: {
            name: { show: true, required: true },
            ssn: { show: true, required: true },
            vaFileNumber: { show: true, required: false },
            dateOfBirth: { show: true, required: false },
          },
          dataAdapter: {
            ssnPath: 'ssnLast4',
            vaFileNumberPath: 'vaFileNumberLast4',
          },
          note: <PersonalInformationNote />,
        }),
        payeeNumber: {
          path: 'payee-number',
          title: 'Payee Number',
          uiSchema: payeeNumber.uiSchema,
          schema: payeeNumber.schema,
          depends: formData =>
            formData?.vaBenefitProgram === 'chapter35' &&
            !!formData.vaFileNumber,
        },
        ...profileContactInfoPages({
          contactInfoRequiredKeys: ['mailingAddress'],
          content: {
            ...getContent('request'),
            title: 'Confirm the contact information we have on file for you',
            description: null,
          },
          prefillPatternEnabled: false,
        }),
      },
    },
    examInformationChapter: {
      title: 'Exam information',
      pages: {
        examNameAndDateTaken: {
          path: 'exam-name-and-date-taken',
          title: 'Exam name and date taken',
          uiSchema: examNameAndDateTaken.uiSchema,
          schema: examNameAndDateTaken.schema,
        },
        organizationInfo: {
          path: 'organization-info',
          title: 'Name and address of organization issuing the exam',
          uiSchema: organizationInfo.uiSchema,
          schema: organizationInfo.schema,
          initialData: {
            organizationAddress: { country: 'USA' },
          },
        },
        examCost: {
          path: 'exam-cost',
          title: 'Exam cost',
          uiSchema: examCost.uiSchema,
          schema: examCost.schema,
        },
      },
    },
    remarksChapter: {
      title: 'Remarks',
      pages: {
        remarksPage: {
          path: 'remarks',
          title: 'Remarks',
          uiSchema: remarks.uiSchema,
          schema: remarks.schema,
        },
      },
    },
    supportingDocumentsChapter: {
      title: 'Supporting documents',
      pages: {
        supportingDocuments: {
          path: 'supporting-documents',
          title: 'Supporting documents upload',
          uiSchema: supportingDocuments.uiSchema,
          schema: supportingDocuments.schema,
        },
      },
    },
  },
  // getHelp,
  footerContent,
};

export default formConfig;
